import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';

import {
  useGetMachine,
  useGetMachineShares,
  useCreateMachineShare,
  useRevokeMachineShare,
  getGetMachineSharesQueryKey,
  useGetGuestMachine,
  useConnectAsGuest,
  useForkMachine,
  useToggleFavorite,
  useShutdownMachine,
  useDeleteMachine,
  getGetMachineQueryKey,
} from '@/lib/api/orval/target/machines/machines';
import { useAuthStore } from '@/stores/authStore';
import { errorMessage, guestMachineLimit, type GuestMachineLimit } from '@/lib/errors';
import { xpraPath } from '@/lib/xpra';

function SessionSharing({
  publicId,
  inviteUrl,
  onCopy,
  copied,
}: {
  publicId: string;
  inviteUrl: string;
  onCopy: () => void;
  copied: boolean;
}): React.ReactElement {
  const queryClient = useQueryClient();
  const { data } = useGetMachineShares(publicId);
  const refresh = (): void => {
    queryClient.invalidateQueries({ queryKey: getGetMachineSharesQueryKey(publicId) });
  };
  const create = useCreateMachineShare({ mutation: { onSuccess: refresh } });
  const revoke = useRevokeMachineShare({ mutation: { onSuccess: refresh } });
  const [copiedShareId, setCopiedShareId] = useState<string | null>(null);

  async function copyShare(id: string, path: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(window.location.origin + path);
      setCopiedShareId(id);
      setTimeout(() => setCopiedShareId(null), 1500);
    } catch {
      /* clipboard unavailable; the link is selectable in the field */
    }
  }

  const shares = data?.shares ?? [];
  const [mode, setMode] = useState<'full' | 'readonly'>('readonly');

  return (
    <div className="themed-card p-4 flex flex-col gap-3">
      <div className="flex items-center justify-between gap-4">
        <h3 className="font-semibold">Session sharing</h3>
        <select aria-label="Share permissions" className="themed-input text-sm"
          value={mode} onChange={(event) => setMode(event.target.value as 'full' | 'readonly')}>
          <option value="readonly">View only</option>
          <option value="full">Allow control</option>
        </select>
        <button
          className="btn-secondary text-sm"
          onClick={() => create.mutate({ publicId, data: { mode } })}
          disabled={create.isPending}
        >
          {create.isPending ? 'Creating…' : 'New share link'}
        </button>
      </div>
      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
        Choose whether visitors can only view the desktop or control it. Links work until you revoke them.
      </p>
      {create.isError && (
        <p className="text-sm" style={{ color: 'var(--danger)' }}>
          {errorMessage(create.error)}
        </p>
      )}
      {shares.length === 0 ? (
        <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
          No share links.
        </p>
      ) : (
        <ul className="flex flex-col gap-2">
          {shares.map((share) => (
            <li key={share.id} className="flex gap-2 items-center">
              <span className="text-sm">{share.mode === 'readonly' ? 'View only' : 'Control'}</span>
              <input
                className="themed-input text-sm"
                readOnly
                value={
                  typeof window === 'undefined'
                    ? share.share_path
                    : window.location.origin + share.share_path
                }
              />
              <button
                className="btn-secondary text-sm"
                onClick={() => copyShare(share.id, share.share_path)}
              >
                {copiedShareId === share.id ? 'Copied!' : 'Copy'}
              </button>
              <button
                className="btn-secondary text-sm"
                style={{ color: 'var(--danger)' }}
                onClick={() => revoke.mutate({ publicId, shareId: share.id })}
                disabled={revoke.isPending}
              >
                Revoke
              </button>
            </li>
          ))}
        </ul>
      )}
      {inviteUrl && (
        <div className="flex flex-col gap-1 pt-1">
          <span className="text-sm" style={{ color: 'var(--text-secondary)' }}>
            Invite link: gives each visitor their own share link, and the share code that
            issues them can be changed on the edit page.
          </span>
          <div className="flex gap-2">
            <input className="themed-input text-sm" readOnly value={inviteUrl} />
            <button className="btn-secondary text-sm" onClick={onCopy}>
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function MachineProfile(): React.ReactElement {
  const { publicId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [copied, setCopied] = useState(false);

  const { data: machine, isLoading, error } = useGetMachine(publicId, {
    query: { enabled: Boolean(publicId) },
  });

  const [limit, setLimit] = useState<GuestMachineLimit | null>(null);
  const { data: guest } = useGetGuestMachine();
  const connect = useConnectAsGuest({
    mutation: {
      onSuccess: (result) =>
        navigate(xpraPath(result.wss_uri), {
          state: { wssUri: result.wss_uri, redirectUrl: `/m/${publicId}` },
        }),
    },
  });
  const fork = useForkMachine({
    mutation: {
      onError: (e) => setLimit(guestMachineLimit(e)),
      onSuccess: (result) =>
        navigate(xpraPath(result.wss_uri), {
          state: {
            wssUri: result.wss_uri,
            machine: result.machine,
            redirectUrl: `/m/${result.machine.public_id}`,
          },
        }),
    },
  });

  const favorite = useToggleFavorite({
    mutation: {
      onSuccess: () =>
        queryClient.invalidateQueries({ queryKey: getGetMachineQueryKey(publicId) }),
    },
  });

  const shutdown = useShutdownMachine();

  const remove = useDeleteMachine({
    // A guest has no machine list to go back to.
    mutation: { onSuccess: () => navigate(user ? '/machines/mine' : '/') },
  });

  if (isLoading) return <p style={{ color: 'var(--text-secondary)' }}>Loading…</p>;
  if (error) return <p style={{ color: 'var(--danger)' }}>{errorMessage(error)}</p>;
  if (!machine) return <p>Machine not found.</p>;

  const isOwner = Boolean(
    user && machine.owner && machine.owner.username === user.username,
  );

  // A guest may have one machine; forking this one would have to give up the
  // very machine being forked.
  const isOwnGuestMachine = guest?.machine?.public_id === machine.public_id;

  // Whoever the machine belongs to: its account owner, or the guest who made it.
  const canControl = isOwner || Boolean(machine.can_connect_as_guest);

  const shareUrl =
    machine.secret_share_code && typeof window !== 'undefined'
      ? `${window.location.origin}/m/${machine.public_id}/share/${machine.secret_share_code}`
      : '';

  async function copyShare(): Promise<void> {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable */
    }
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">{machine.name}</h1>
          {machine.owner && (
            <p style={{ color: 'var(--text-secondary)' }}>by @{machine.owner.username}</p>
          )}
          {machine.short_description && <p className="mt-2">{machine.short_description}</p>}
        </div>
        <div className="flex flex-col gap-2 shrink-0">
          {machine.can_connect_as_guest && (
            <button
              className="btn-primary"
              onClick={() => connect.mutate({ publicId })}
              disabled={connect.isPending}
            >
              {connect.isPending ? 'Connecting…' : 'Connect'}
            </button>
          )}
          <button
            className={machine.can_connect_as_guest ? 'btn-secondary' : 'btn-primary'}
            onClick={() => fork.mutate({ publicId, data: {} })}
            disabled={fork.isPending || isOwnGuestMachine}
            title={
              isOwnGuestMachine
                ? 'This is your demo machine, and a guest may have only one: ' +
                  'forking it would replace the machine being forked.'
                : undefined
            }
          >
            {fork.isPending ? 'Forking…' : 'Fork & launch'}
          </button>
          {limit?.can_replace && (
            <div className="themed-card p-2 flex flex-col gap-1">
              <span className="text-xs">
                Replace your demo machine {limit.existing_machine.name} with this fork?
              </span>
              <button
                className="btn-primary text-xs"
                onClick={() => fork.mutate({ publicId, data: { replace: true } })}
                disabled={fork.isPending}
              >
                Replace it
              </button>
            </div>
          )}
          {user && (
            <button className="btn-secondary" onClick={() => favorite.mutate({ publicId })}>
              {machine.is_favorited ? '★ Starred' : '☆ Star'} ({machine.star_count ?? 0})
            </button>
          )}
          {isOwner && (
            <>
              <Link to={`/m/${publicId}/edit`} className="btn-secondary text-center">
                Edit
              </Link>
              <button className="btn-secondary" onClick={() => shutdown.mutate({ publicId })}>
                {shutdown.isPending ? 'Shutting down…' : 'Shut down'}
              </button>
            </>
          )}
          {canControl &&
            (!confirmingDelete ? (
              <button
                className="btn-secondary"
                style={{ color: 'var(--danger)' }}
                onClick={() => setConfirmingDelete(true)}
              >
                Delete
              </button>
            ) : (
              <div className="flex flex-col gap-1 themed-card p-2">
                <span className="text-xs">Delete this machine and its storage?</span>
                <div className="flex gap-1">
                  <button
                    className="btn-primary text-xs"
                    style={{ backgroundColor: 'var(--danger)' }}
                    onClick={() => remove.mutate({ publicId })}
                    disabled={remove.isPending}
                  >
                    Yes, delete
                  </button>
                  <button
                    className="btn-secondary text-xs"
                    onClick={() => setConfirmingDelete(false)}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ))}
        </div>
      </div>

      {fork.isError && (
        <p style={{ color: 'var(--danger)' }}>{errorMessage(fork.error)}</p>
      )}

      <div className="flex gap-6 text-sm" style={{ color: 'var(--text-secondary)' }}>
        <span>⭐ {machine.star_count ?? 0} stars</span>
        <span>🍴 {machine.fork_count ?? 0} forks</span>
        {machine.hosted_at && <span>🖥 {machine.hosted_at}</span>}
      </div>

      {canControl && (
        <SessionSharing publicId={publicId} inviteUrl={shareUrl} onCopy={copyShare} copied={copied} />
      )}

      {machine.tags.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {machine.tags.map((tag) => (
            <span
              key={tag}
              className="text-xs px-2 py-0.5 rounded-full"
              style={{ backgroundColor: 'var(--bg-secondary)' }}
            >
              {tag}
            </span>
          ))}
        </div>
      )}

      {machine.images && machine.images.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2">
          {machine.images.map((img) => (
            <img
              key={img.public_id}
              src={`/media/${img.image_path}`}
              alt={img.caption || machine.name}
              className="rounded-lg border w-full"
              style={{ borderColor: 'var(--border)' }}
            />
          ))}
        </div>
      )}

      {machine.readme_html ? (
        <article
          className="themed-card p-6"
          dangerouslySetInnerHTML={{ __html: machine.readme_html }}
        />
      ) : (
        <p style={{ color: 'var(--text-secondary)' }}>No README yet.</p>
      )}
    </div>
  );
}
